import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { isMedia360Video, sortMediaWith360First, TDR } from 'tdr-common';
import { useViewToggle } from '../../context/ViewToggleProvider';
import { useMatterport } from '../../context/new/MatterportContext';
import { analytics, MEDIA_CAROUSEL_SWIPE, MEDIA_CAROUSEL_DOT_CLICK, MEDIA_CAROUSEL_NAV_CLICK, MEDIA_ITEM_VIEWED } from '../../helpers/analytics';
import { getCdnImageUri, getCdnVideoUri } from '../../helpers/getCDNURI';
import { useScreenSize } from '../../hooks/useScreenSize';
import { BookingFlowVariant } from '../table-drawer/TableDrawer';
import VideoPlayer from '../video-player/VideoPlayer';
import { getVideoElementID } from '../video-player/helper';
import { VirtualViewButton } from './VirtualViewButton';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { type CarouselApi } from '@/components/ui/carousel';
import Icon3D from '../../icons/3D.svg';

type MediaCarouselProps = {
	tableLabel: string;
	items: TDR.MediaContent[];
	itemHeight: number;
	itemWidth: number;
	brightness?: number;
	contrast?: number;
	variant?: BookingFlowVariant;
};

const MediaCarousel = ({ tableLabel, items = [], itemHeight, itemWidth, brightness, contrast, variant = 'default' }: MediaCarouselProps) => {
	const { isSmallScreen } = useScreenSize();
	const { toggleView } = useViewToggle();
	const { isError: matterportError, isLoading: matterportLoading } = useMatterport();
	const videosRefs = useRef<HTMLVideoElement[]>([]);

	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const sortedItems = useMemo(() => sortMediaWith360First(items), [items]);

	const showVirtualViewButton = isSmallScreen && (!matterportLoading && !matterportError) && variant === 'default';
	const first360Video = sortedItems.find(isMedia360Video);
	const has360Video = !!first360Video;
	const enableMobileInline360 = isSmallScreen && has360Video;
	const renderMedia = useMemo(() => {
		if (!enableMobileInline360) {
			return sortedItems;
		}
		return sortedItems.filter((m) => !isMedia360Video(m));
	}, [sortedItems, enableMobileInline360]);

	const renderMediaRef = useRef(renderMedia);
	renderMediaRef.current = renderMedia;

	const [api, setApi] = useState<CarouselApi>();

	const [currentPage, setCurrentPage] = useState<number>(0);
	const isInitialMount = useRef(true);

	const hideNavButtons = renderMedia.length < 2 || (isVideoPlaying && !isHovered);

	useEffect(() => {
		videosRefs.current = videosRefs.current.slice(0, renderMedia.length);
	}, [renderMedia]);

	useEffect(() => {
		if (!api) {
			return;
		}

		const initialPage = api.selectedScrollSnap() + 1;
		const initialIndex = api.selectedScrollSnap();
		setCurrentPage(initialPage);
		isInitialMount.current = true;

		const rm = renderMediaRef.current;
		const initialMediaItem = rm[initialIndex];
		if (initialMediaItem) {
			const mediaTags = initialMediaItem.tags || ((initialMediaItem as any).tag ? [(initialMediaItem as any).tag] : []);
			analytics.track(MEDIA_ITEM_VIEWED, {
				type: initialMediaItem.type,
				tags: mediaTags,
				page: initialPage,
				totalPages: rm.length
			});
		}

		api.on('select', () => {
			const newPage = api.selectedScrollSnap() + 1;
			const selectedIndex = api.selectedScrollSnap();
			setCurrentPage(newPage);
			handleChange(selectedIndex);

			const media = renderMediaRef.current;
			const currentMediaItem = media[selectedIndex];
			if (currentMediaItem) {
				const mediaTags = currentMediaItem.tags || ((currentMediaItem as any).tag ? [(currentMediaItem as any).tag] : []);
				analytics.track(MEDIA_ITEM_VIEWED, {
					type: currentMediaItem.type,
					tags: mediaTags,
					page: newPage,
					totalPages: media.length
				});
			}

			if (!isInitialMount.current) {
				analytics.track(MEDIA_CAROUSEL_SWIPE, {
					page: newPage,
					totalPages: media.length,
					tableLabel
				});
			}
			isInitialMount.current = false;
		});
	}, [api, renderMedia.length, tableLabel]);

	const handleChange = (index: number) => {
		if (index) {
			try {
				videosRefs?.current?.forEach((v, i) => {
					if (!v || !v.isConnected) {
						return;
					}
					if (i !== index) {
						try {
							v.pause();
						}
						catch {
						}
					}
					else {
						v.play()?.catch(() => {
						});
					}
				});
			}
			catch {
			}
		}
	};

	const handleNavClick = (e: React.MouseEvent, direction: 'next' | 'prev') => {
		e.preventDefault();

		analytics.track(MEDIA_CAROUSEL_NAV_CLICK, {
			direction,
			currentPage,
			totalPages: renderMedia.length,
			tableLabel
		});
	};

	const handleDotClick = (index: number) => {
		api.scrollTo(index);
		const currentMediaItem = renderMedia[index];
		if (currentMediaItem) {
			const mediaTags = currentMediaItem.tags || ((currentMediaItem as any).tag ? [(currentMediaItem as any).tag] : []);
			analytics.track(MEDIA_ITEM_VIEWED, {
				type: currentMediaItem.type,
				tags: mediaTags,
				page: index + 1,
				totalPages: renderMedia.length
			});
		}
		analytics.track(MEDIA_CAROUSEL_DOT_CLICK, {
			page: index + 1,
			totalPages: renderMedia.length,
			tableLabel
		});
	};

	const mediaControl = () => {
		return (
			<div className="absolute left-0 right-0 bottom-2 p-2 flex justify-center items-center gap-1 rounded-3xl bg-carsousel-buttons backdrop-blur-md w-fit mx-auto">
				{renderMedia.map((_, index) =>
					<div key={index} className={`w-[6px] h-[6px] min-w-[6px] min-h-[6px] border rounded-full !box-content cursor-pointer ${currentPage === index + 1 ? 'border-carousel-selected bg-carousel-selected bg-clip-border' : 'bg-tablz-light-gray-4 border-transparent bg-clip-content'}`} onClick={() => handleDotClick(index)} />
				)}
			</div>
		);
	};

	return (
		<div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className="relative h-full">
			<Carousel
				setApi={setApi}
				orientation='horizontal'
				className='h-full overflow-hidden'
				opts={{
					loop: true
				}}
			>
				<CarouselContent outerClassNames='h-full' className='h-full'>
					{renderMedia.map((item, idx) => {
						if (item.type === TDR.MediaType.IMAGE) {
							return (
								<CarouselItem key={`${idx} - ${item.path}`} className="h-full">
									<React.Fragment >
										{idx === 0 && enableMobileInline360 ? (
											<div className="relative h-full w-full" onClick={toggleView}>
												<VideoPlayer
													src={first360Video ? getCdnVideoUri((first360Video as any).path, {
														height: itemHeight,
														fit: 'contain',
														duration: '10s'
													}) : ''}
													onPlaybackChange={() => {}}
													hideControls={true}
													style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
													autoPlay={true}
													muted={true}
													loop={true}
													playsInline={true}
												/>
												<div className="absolute bottom-2 right-2">
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation(); toggleView();
														}}
														className="p-3 bg-white/10 rounded-xl outline outline-1 outline-offset-[-1px] outline-white/10 backdrop-blur-md inline-flex justify-center items-center gap-2 overflow-hidden"
														aria-label="Open 3D view"
													>
														<img src={Icon3D} alt="3D view" className="w-6 h-6" />
													</button>
												</div>
											</div>
										) : (
											<>
												{idx === 0 && showVirtualViewButton && <VirtualViewButton onClick={toggleView} type={tableLabel} />}
												<img
													src={getCdnImageUri(item.path, {
														height: itemHeight,
														width: itemWidth,
														fit: 'crop'
													})}
													alt={`Item ${idx + 1}`}
													className="h-full w-full object-cover"
													style={
														idx === 0 && showVirtualViewButton
															? {
																filter: `brightness(${brightness ?? 1}) contrast(${contrast ?? 1})`
															}
															: {}
													}
												/>
											</>
										)}
									</React.Fragment>
								</CarouselItem>
							);
						}
						else if (item.type === TDR.MediaType.VIDEO) {
							return (
								<CarouselItem key={`${idx} - ${item.path}`} className="h-full">
									<VideoPlayer
										id={getVideoElementID(idx)}
										ref={(el) => (videosRefs.current[idx] = el)}
										src={getCdnVideoUri(item.path, {
											height: itemHeight,
											fit: 'contain'
										})}
										onPlaybackChange={setIsVideoPlaying}
									/>
								</CarouselItem>
							);
						}
					})}
				</CarouselContent>
				{hideNavButtons ? null : <CarouselNext className={'right-2 z-[1] bg-carsousel-buttons border-[1.5px] border-tablz-med-gray-4 backdrop-blur-md hover:border-tablz-light-gray-4'} onClick={(e) => {
					handleNavClick(e, 'next'); api.scrollNext();
				}} />}
				{hideNavButtons ? null : <CarouselPrevious className={'left-2 z-[1] bg-carsousel-buttons border-[1.5px] border-tablz-med-gray-4 backdrop-blur-md hover:border-tablz-light-gray-4'} onClick={(e) => {
					handleNavClick(e, 'prev'); api.scrollPrev();
				}} />}
				{mediaControl()}
			</Carousel>
		</div>
	);
};

export default MediaCarousel;

